import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';

import api from '../../api/axios';
import { StatusBadge, PriorityBadge } from '../../components/StatusBadge';

const Field = ({ label, value }) => (
  <div>
    <p className="text-xs text-gray-400 mb-0.5">{label}</p>
    <p className="text-sm font-medium text-gray-800">{value || '—'}</p>
  </div>
);

const formatDuration = (from, to) => {
  if (!from) return '—';
  const start = new Date(from);
  const end = to ? new Date(to) : new Date();
  const mins = Math.floor((end - start) / 60000);
  if (mins < 60) return `${mins} min`;
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  if (h < 24) return m > 0 ? `${h}h ${m}m` : `${h}h`;
  const d = Math.floor(h / 24);
  const rh = h % 24;
  return rh > 0 ? `${d}d ${rh}h` : `${d}d`;
};

const AdminTicketDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [ticket, setTicket] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    api.get(`/api/admin/tickets/${id}`)
      .then(res => setTicket(res.data))
      .catch(() => setError('Failed to load ticket details.'))
      .finally(() => setLoading(false));
  }, [id]);

  if (loading) return (
    <div className="min-h-screen bg-gray-50">
      
      <div className="text-center py-20 text-gray-400 text-sm">Loading...</div>
    </div>
  );

  if (error || !ticket) return (
    <div className="min-h-screen bg-gray-50">
      
      <div className="text-center py-20 text-red-400 text-sm">{error || 'Ticket not found.'}</div>
    </div>
  );

  const isImage = ticket.attachmentType === 'IMAGE';

  const actionColor = (action) => {
    if (!action) return 'bg-gray-400';
    if (action.includes('CREATED')) return 'bg-blue-500';
    if (action.includes('RESOLVED')) return 'bg-green-500';
    if (action.includes('STATUS')) return 'bg-amber-500';
    return 'bg-gray-400';
  };

  return (
    <div className="min-h-screen bg-gray-50">
      
      <div className="w-full px-4 py-8">

        {/* Back */}
        <button onClick={() => navigate('/admin/tickets')}
          className="text-sm text-gray-500 hover:text-gray-700 mb-5 flex items-center gap-1">
          ← Back to All Tickets
        </button>

        {/* Header */}
        <div className="flex items-start justify-between mb-6">
          <div>
            <h1 className="text-xl font-bold text-gray-800">{ticket.ticketId}</h1>
            <p className="text-sm text-gray-500 mt-0.5">
              {ticket.enterpriseName} &middot; KAM: {ticket.kamName}
            </p>
          </div>
          <div className="flex gap-2">
            <PriorityBadge priority={ticket.priority} />
            <StatusBadge status={ticket.status} />
          </div>
        </div>

        {/* Ticket Info */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6 mb-5">
          <h2 className="text-sm font-semibold text-gray-600 uppercase tracking-wide mb-4">Ticket Details</h2>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-5 mb-5">
            <Field label="Ticket ID" value={ticket.ticketId} />
            <Field label="Enterprise" value={ticket.enterpriseName} />
            <Field label="Enterprise ID" value={ticket.enterpriseId} />
            <Field label="Raised By (KAM)" value={ticket.kamName} />
            <Field label="KAM ID" value={ticket.kamId} />
            <Field label="Ticket Type" value={ticket.ticketType} />
            <Field label="Priority" value={ticket.priority} />
            <Field label="Status" value={ticket.status} />
            <Field label="Assigned Agent" value={ticket.assignedAgentName} />
            <Field label="Created At" value={ticket.createdAt ? new Date(ticket.createdAt).toLocaleString() : null} />
            <Field label="Last Updated" value={ticket.updatedAt ? new Date(ticket.updatedAt).toLocaleString() : null} />
            <Field label="Resolved At" value={ticket.resolvedAt ? new Date(ticket.resolvedAt).toLocaleString() : null} />
            <div className="col-span-1 md:col-span-3">
              <p className="text-xs text-gray-400 mb-1">
                {ticket.status === 'RESOLVED' ? 'Total Time to Resolve' : 'Time Elapsed (ongoing)'}
              </p>
              <span className={`inline-flex items-center gap-1.5 text-sm font-semibold px-3 py-1 rounded-full ${
                ticket.status === 'RESOLVED'
                  ? 'bg-green-50 text-green-700'
                  : 'bg-amber-50 text-amber-700'
              }`}>
                {ticket.status !== 'RESOLVED' && (
                  <span className="w-1.5 h-1.5 rounded-full bg-amber-500 animate-pulse" />
                )}
                {formatDuration(ticket.createdAt, ticket.resolvedAt)}
                {ticket.status !== 'RESOLVED' && <span className="font-normal opacity-70">since raised</span>}
              </span>
            </div>
          </div>

          {/* Description */}
          <div className="mb-4">
            <p className="text-xs text-gray-400 mb-1">Description</p>
            <p className="text-sm text-gray-700 bg-gray-50 rounded-lg p-3 whitespace-pre-wrap">{ticket.description}</p>
          </div>

          {/* AI Reason */}
          {ticket.aiReason && (
            <div className="bg-blue-50 border border-blue-100 rounded-lg p-3 mb-4">
              <p className="text-xs font-semibold text-blue-600 mb-1">AI Classification Reason</p>
              <p className="text-xs text-blue-700">{ticket.aiReason}</p>
            </div>
          )}

          {/* Resolution */}
          {ticket.status === 'RESOLVED' && ticket.resolution && (
            <div className="bg-green-50 border border-green-200 rounded-lg p-3 mb-4">
              <p className="text-xs font-semibold text-green-700 mb-1">Resolution</p>
              <p className="text-sm text-green-700">{ticket.resolution}</p>
            </div>
          )}

          {/* Attachment */}
          {ticket.attachmentUrl && (
            <div>
              <p className="text-xs text-gray-400 mb-2">Attachment</p>
              {isImage ? (
                <div className="border border-gray-200 rounded-xl overflow-hidden inline-block max-w-full">
                  <img
                    src={`http://localhost:8081${ticket.attachmentUrl}`}
                    alt="Ticket attachment"
                    className="max-w-full max-h-72 object-contain"
                  />
                </div>
              ) : (
                <a href={`http://localhost:8081${ticket.attachmentUrl}`} target="_blank" rel="noreferrer"
                  className="inline-flex items-center gap-2 text-sm text-primary hover:text-primary-dark bg-blue-50 px-4 py-2 rounded-lg">
                  View PDF Attachment
                </a>
              )}
            </div>
          )}
        </div>

        {/* History / Timeline */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
          <h2 className="text-sm font-semibold text-gray-600 uppercase tracking-wide mb-5">
            Ticket History
            {ticket.history?.length > 0 && (
              <span className="ml-2 text-xs font-normal text-gray-400 normal-case">
                {ticket.history.length} event{ticket.history.length !== 1 ? 's' : ''}
              </span>
            )}
          </h2>

          {(!ticket.history || ticket.history.length === 0) ? (
            <p className="text-sm text-gray-400 text-center py-6">No history available.</p>
          ) : (
            <div className="relative">
              <div className="absolute left-1.5 top-2 bottom-2 w-0.5 bg-gray-100" />
              <div className="space-y-6">
                {ticket.history.map((h, i) => (
                  <div key={i} className="flex gap-4 relative">
                    <div className={`flex-shrink-0 w-3.5 h-3.5 rounded-full mt-0.5 border-2 border-white shadow-sm z-10 ${actionColor(h.action)}`} />
                    <div className="flex-1">
                      <div className="flex flex-wrap items-center gap-2 mb-1">
                        <p className="text-sm font-semibold text-gray-800">
                          {(h.action || '').replace(/_/g, ' ')}
                        </p>
                        <span className="text-xs bg-gray-100 text-gray-500 px-2 py-0.5 rounded-full">
                          {h.doneByRole}
                        </span>
                      </div>
                      {h.note && (
                        <p className="text-sm text-gray-600 mb-1">{h.note}</p>
                      )}
                      <p className="text-xs text-gray-400">
                        {h.doneBy}
                        {h.createdAt ? ` · ${new Date(h.createdAt).toLocaleString()}` : ''}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

      </div>
    </div>
  );
};

export default AdminTicketDetail;
